// ═══════════════════════════════════════════════════════════
// Public Portal — Home / Landing Page
// ═══════════════════════════════════════════════════════════

import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  IconFileText, IconSearch, IconArrowRight, IconClock,
  IconShieldCheck, IconMail, IconChevronRight,
} from '@tabler/icons-react';
import { useJurisdiction } from '../../contexts/JurisdictionContext';
import { queryRecords, fetchRecord } from '../../api/client';
import { TABLES } from '../../api/tables';
import { FIELDS } from '../../api/fields';

function parseContactInfo(raw?: string): { email?: string; phone?: string; address?: string } {
  if (!raw) return {};
  try {
    const parsed = JSON.parse(raw);
    if (typeof parsed === 'object' && parsed !== null) return parsed;
  } catch { /* not JSON — treat as plain string */ }
  // plain string: just try to extract email
  const email = raw.match(/[\w.-]+@[\w.-]+\.\w+/)?.[0];
  return { email, address: email ? undefined : raw };
}

const FAQ = [
  {
    q: 'What is a public records request?',
    a: 'Public records laws give you the right to request copies of records held by government agencies. This includes documents, emails, contracts, meeting minutes, and more.',
  },
  {
    q: 'How long does it take to get a response?',
    a: 'Response timelines are set by your jurisdiction\'s public records law. You\'ll receive an acknowledgment promptly, and we\'ll keep you updated as your request is processed.',
  },
  {
    q: 'Is there a fee to submit a request?',
    a: 'Submitting a request is free. Fees may apply for research time, copying, or large document packages. You will be notified before any fees are assessed.',
  },
  {
    q: 'Can I request records anonymously?',
    a: 'Yes. You are not required to identify yourself or explain your reason for requesting records.',
  },
];

export default function PortalHome() {
  const [trackNumber, setTrackNumber] = useState('');
  const navigate = useNavigate();
  const jurisdiction = useJurisdiction();

  const [recentlyReleased, setRecentlyReleased] = useState<Array<{
    id: string; title: string; category: string; date: string; fileName: string;
  }>>([]);

  useEffect(() => {
    async function loadReleased() {
      try {
        const { records } = await queryRecords(TABLES.documents, {
          filters: [{ field: FIELDS.documents.isReleased, operator: 'eq', value: true }],
          sort: { field: FIELDS.documents.releasedAt, direction: 'desc' },
          limit: 6,
        }, { anonymous: true });

        const docs = records as Record<string, unknown>[];
        const items = await Promise.all(docs.map(async (doc) => {
          const requestId = doc[FIELDS.documents.requestId] as number;
          const releasedAt = String(doc[FIELDS.documents.releasedAt] ?? '');
          const fileName = String(doc[FIELDS.documents.fileName] ?? '');
          try {
            const req = await fetchRecord<Record<string, unknown>>(
              TABLES.requests,
              requestId,
              { anonymous: true },
            );
            const subject = req ? String(req[FIELDS.requests.subject] ?? fileName) : fileName;
            return { id: String(doc['record_id']), title: subject, category: 'Public Records', date: releasedAt, fileName };
          } catch {
            return { id: String(doc['record_id']), title: fileName, category: 'Public Records', date: releasedAt, fileName };
          }
        }));
        setRecentlyReleased(items.filter(i => i.title));
      } catch { /* silent */ }
    }
    loadReleased();
  }, []);

  function onTrack(e: React.FormEvent) {
    e.preventDefault();
    if (trackNumber.trim()) {
      navigate(`/portal/track/${encodeURIComponent(trackNumber.trim())}`);
    }
  }

  const displayRecords = recentlyReleased;

  return (
    <div>

      {/* ── Page header ── */}
      <div style={{ background: 'white', borderBottom: '1px solid var(--color-neutral-200)', padding: '40px 24px 36px' }}>
        <div style={{ maxWidth: 860, margin: '0 auto' }}>
          <p style={{ fontSize: 12, fontWeight: 600, color: 'var(--color-primary-700)', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 8 }}>
            {jurisdiction?.name ?? 'Public Records'}
          </p>
          <h1 style={{ fontSize: 24, fontWeight: 700, color: 'var(--color-neutral-900)', margin: '0 0 8px', lineHeight: 1.25 }}>
            Public Records Requests
          </h1>
          <p style={{ fontSize: 16, color: 'var(--color-neutral-500)', margin: '0 0 28px', lineHeight: 1.6, maxWidth: 560 }}>
            Submit, track, and receive records from {jurisdiction?.name ?? 'the public records office'}. {jurisdiction?.lawName ?? 'Freedom of Information Act'}.
          </p>

          <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'center' }}>
            <Link to="/portal/submit" className="btn btn-primary" style={{ gap: 7 }}>
              <IconFileText size={15} /> Submit a Request
            </Link>
            <Link to="/portal/track" className="btn btn-secondary" style={{ gap: 7 }}>
              <IconSearch size={15} /> Track My Request
            </Link>

            <div style={{ width: 1, height: 28, background: 'var(--color-neutral-200)', margin: '0 4px' }} />

            {/* Quick track inline */}
            <form onSubmit={onTrack} style={{ display: 'flex', gap: 0 }}>
              <input
                type="text"
                className="form-input"
                style={{ borderRadius: '4px 0 0 4px', fontSize: 14, width: 220 }}
                placeholder="e.g. 2026-A4BC12"
                value={trackNumber}
                onChange={e => setTrackNumber(e.target.value)}
              />
              <button
                type="submit"
                className="btn btn-secondary"
                style={{ borderRadius: '0 4px 4px 0', borderLeft: 'none', fontSize: 14 }}
              >
                Track
              </button>
            </form>
          </div>
        </div>
      </div>

      {/* ── Feature callouts ── */}
      <div style={{ padding: '28px 24px', background: 'var(--color-neutral-50)', borderBottom: '1px solid var(--color-neutral-200)' }}>
        <div style={{ maxWidth: 860, margin: '0 auto', display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 16 }}>
          {[
            { icon: <IconClock size={18} />, title: 'Real-Time Status', desc: 'Track your request at any time and see exactly where it is in the review process.' },
            { icon: <IconShieldCheck size={18} />, title: 'Secure & Private', desc: 'Submit anonymously or create an account to manage your full request history.' },
            { icon: <IconMail size={18} />, title: 'Email Updates', desc: 'Receive automatic notifications as your request progresses and when records are ready.' },
          ].map(f => (
            <div key={f.title} style={{ background: 'white', borderRadius: 4, padding: '16px 18px', border: '1px solid var(--color-neutral-200)', display: 'flex', gap: 12, alignItems: 'flex-start' }}>
              <div style={{ width: 34, height: 34, borderRadius: 4, background: 'var(--color-primary-50, #F7F8FD)', color: 'var(--color-primary-700)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                {f.icon}
              </div>
              <div>
                <div style={{ fontSize: 14, fontWeight: 600, color: 'var(--color-neutral-800)', marginBottom: 4 }}>{f.title}</div>
                <div style={{ fontSize: 12, color: 'var(--color-neutral-500)', lineHeight: 1.6 }}>{f.desc}</div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* ── Recently released records ── */}
      {displayRecords.length > 0 && (
        <div style={{ padding: '32px 24px' }}>
          <div style={{ maxWidth: 860, margin: '0 auto' }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16 }}>
              <h2 style={{ fontSize: 16, fontWeight: 700, color: 'var(--color-neutral-800)', margin: 0 }}>
                Recently Released Records
              </h2>
              <Link to="/portal/library" style={{ fontSize: 12, color: 'var(--color-primary-700)', textDecoration: 'none', display: 'flex', alignItems: 'center', gap: 2 }}>
                View library <IconChevronRight size={12} />
              </Link>
            </div>
            <div style={{ border: '1px solid var(--color-neutral-200)', borderRadius: 4, overflow: 'hidden', background: 'white' }}>
              {displayRecords.map((rec, i) => (
                <div key={rec.id} style={{
                  padding: '12px 16px', display: 'flex', alignItems: 'center', gap: 16,
                  borderBottom: i < displayRecords.length - 1 ? '1px solid var(--color-neutral-100)' : 'none',
                }}>
                  <IconFileText size={16} style={{ color: 'var(--color-primary-700)', flexShrink: 0 }} />
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontSize: 14, fontWeight: 500, color: 'var(--color-neutral-800)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {rec.title}
                    </div>
                    <div style={{ fontSize: 12, color: 'var(--color-neutral-400)', marginTop: 2 }}>
                      {rec.category} · Released {rec.date ? new Date(rec.date).toLocaleDateString() : ''}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* ── FAQ ── */}
      <div style={{ padding: '0 24px 40px' }}>
        <div style={{ maxWidth: 860, margin: '0 auto' }}>
          <h2 style={{ fontSize: 16, fontWeight: 700, color: 'var(--color-neutral-800)', margin: '0 0 14px' }}>
            Frequently Asked Questions
          </h2>
          <div style={{ border: '1px solid var(--color-neutral-200)', borderRadius: 4, overflow: 'hidden', background: 'white' }}>
            {FAQ.map((item, i) => (
              <div key={i} style={{
                padding: '14px 16px',
                borderBottom: i < FAQ.length - 1 ? '1px solid var(--color-neutral-100)' : 'none',
              }}>
                <div style={{ fontSize: 14, fontWeight: 600, color: 'var(--color-neutral-800)', marginBottom: 5 }}>{item.q}</div>
                <div style={{ fontSize: 14, color: 'var(--color-neutral-500)', lineHeight: 1.65 }}>{item.a}</div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* ── Footer ── */}
      <div style={{ padding: '20px 24px', textAlign: 'center', fontSize: 12, color: 'var(--color-neutral-400)', borderTop: '1px solid var(--color-neutral-200)', background: 'white' }}>
        {(() => {
          const contact = parseContactInfo(jurisdiction?.contactInfo);
          const parts: React.ReactNode[] = [];
          if (jurisdiction?.name) parts.push(jurisdiction.name);
          parts.push('Public Records Office');
          if (contact.email) parts.push(<a key="email" href={`mailto:${contact.email}`} style={{ color: 'var(--color-neutral-400)' }}>{contact.email}</a>);
          if (contact.phone) parts.push(contact.phone);
          if (contact.address) parts.push(contact.address);
          return parts.map((p, i) => (
            <span key={i}>{i > 0 && <span style={{ margin: '0 6px', opacity: 0.4 }}>·</span>}{p}</span>
          ));
        })()}
        <span style={{ margin: '0 6px', opacity: 0.4 }}>·</span>
        <Link to="/login" style={{ color: 'var(--color-neutral-400)' }}>Staff login</Link>
      </div>

    </div>
  );
}
